import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { getApi, putApi, postApi } from "../../utils/api";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import * as Yup from "yup";
import validations from "../../config/validation";
import { formatDate } from "../../utils/helpers";
import Footer from "../../components/Footer";
import SortableTable from "../../components/SortableTable";
import Pagination from "../../components/Pagination";
import {
  GET_EMPLOYEE_LIST,
  API_DEALLOCATE_ACCESS_REQUESTS,
  API_ACCESS_DEALLOCATE_REQUEST,
} from "../../config/Endpoints";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";

const AccessDeallocation = ({ title, description }) => {
  const [employeeList, setEmployeeList] = useState([]);
  const [allocatedAccessList, setAllocatedAccessList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [selectedAccessId, setSelectedAccessId] = useState();
  const accessToken = Cookies.get("accessToken");
  const [isLoading, setIsLoading] = useState(false);
  const [isAccessLoading, setIsAccessLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    employeeId: "",
    reason: "",
  });
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  const columns = [
    { key: "accessType", label: "Access Type" },
    { key: "type", label: "Role" },
    { key: "projectName", label: "Project Name" },
    { key: "reason", label: "Reason" },
    {
      key: "requestedStartDate",
      label: "Requested Start Date",
      sortable: true,
      render: (row) =>
        row.requestedStartDate ? formatDate(row.requestedStartDate) : "-",
    },
    {
      key: "requestedEndDate",
      label: "Requested End Date",
      sortable: true,
      render: (row) =>
        row.requestedEndDate ? formatDate(row.requestedEndDate) : "-",
    },
    {
      key: "accessStartDate",
      label: "Actual Start Date",
      sortable: true,
      render: (row) =>
        row.accessStartDate ? formatDate(row.accessStartDate) : "-",
    },

    {
      key: "action",
      label: "Action",
      sortable: false,
      render: (access, index) => (
        <a
          id={access._original.id}
          style={{ cursor: "pointer" }}
          onClick={(e) => handleChangeAccess(e, access._original.id)}
        >
          <i
            className="fa fa-trash-o"
            style={{ fontSize: "24px", color: "red" }}
          ></i>
        </a>
      ),
    },
  ];

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    setTableData(
      allocatedAccessList.map((item, index) => ({
        accessType: item.accessType?.name || "-",
        type: item.type,
        projectName: item.projectName || "All Projects",
        reason: item.reason || "-",
        requestedStartDate: item.createdAt || null,
        requestedEndDate: item.requestedEndDate || null,
        accessStartDate: item.startDate || null,

        _original: item,
      })) || [],
    );
  }, [allocatedAccessList, currentPage, limit]);

  useEffect(() => {
    setIsListLoading(true);
    fetchList();
  }, []);

  const fetchList = () => {
    getApi(GET_EMPLOYEE_LIST, accessToken)
      .then((res) => {
        if (res.status === 200) setEmployeeList(res.data.data);
        setIsListLoading(false);
      })
      .catch(() => setIsListLoading(false));
  };

  const handleChangeEmployee = async (e) => {
    setIsAccessLoading(true);
    setFormData((prev) => ({ ...prev, employeeId: e.value }));
    setAllocatedAccessList([]);
    setSelectedEmployee(e);
    setCurrentPage(1);

    if (!e.value) {
      setIsAccessLoading(false);
      return;
    }

    fetchAccessList(e.value, currentPage, limit);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAccessList(formData.employeeId, page, limit);
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    fetchAccessList(formData.employeeId, 1, newLimit);
  };

  const fetchAccessList = (id, page, size) => {
    setIsAccessLoading(true);
    getApi(
      `${API_DEALLOCATE_ACCESS_REQUESTS}/${id}?pageSize=${size}&pageNo=${page}&status=APPROVED`,
      accessToken,
    )
      .then((res) => {
        if (res.status === 200) {
          const data = res.data.data?.content || [];
          setAllocatedAccessList(data);
          setTotalPages(res.data.data?.totalPages || 1);
        }
        setIsAccessLoading(false);
      })
      .catch(() => setIsAccessLoading(false));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeAccess = (e, id) => {
    setSelectedAccessId(Number(id));
    document
      .getElementsByClassName("open-modal-access-deallocation")[0]
      .click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      const requestData = { comment: formData.reason.trim() };

      const res = await putApi(
        `${API_ACCESS_DEALLOCATE_REQUEST}/${selectedAccessId}`,
        requestData,
        accessToken,
      );

      if (res?.status === 200 || res?.data?.statusCode === 200) {
        toast.success(res?.data?.message || "Access successfully deallocated!");

        fetchAccessList(formData.employeeId, currentPage, limit);
        setFormData({ ...formData, reason: "" });
        setSelectedAccessId("");

        document.getElementsByClassName("cancel-modal")[0].click();
        return;
      }

      if (res?.data?.message) {
        toast.error(res.data.message);
        return;
      }

      toast.error("Something went wrong. ");
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      }

      if (err.inner) {
        const errors = {};
        err.inner.forEach((e) => (errors[e.path] = e.message));
        setValidationErrors(errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const optionList = employeeList?.map((item) => ({
    value: item.id,
    label: item.empName,
    type: item.employeeType,
  }));

  const Option = (props) => {
    const { data } = props;
    return (
      <components.Option {...props}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{data.label}</span>
          <span style={{ fontSize: "12px", color: "gray" }}>({data.type})</span>
        </div>
      </components.Option>
    );
  };

  return (
    <div>
      {isListLoading && (
        <div className="overlay">
          <div className="mt-5">
            <TailSpin height="100" width="100" color="blue" />
          </div>
        </div>
      )}

      <div className="mb-3">
        <form id="access-deallocation-form">
          <div className="d-f f-w inputs">
            <div className="input-group">
              <div className="row">
                <div className="col-md-3"></div>
                <div className="col-md-6">
                  <div className="dropdown-container">
                    <Select
                      options={optionList}
                      components={{ Option }}
                      onChange={handleChangeEmployee}
                      value={selectedEmployee}
                      placeholder="Select employee"
                      isSearchable={true}
                      isDisabled={employeeList?.length === 0}
                      isRequired={true}
                    />
                  </div>
                </div>
                <div className="col-md-3"></div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {allocatedAccessList.length > 0 && (
        <>
          <SortableTable
            columns={columns}
            data={tableData}
            currentPage={currentPage}
            limit={limit}
          />

          <div className="row">
            <div className="col-md-3">
              <label htmlFor="itemsPerPage">Items per page </label>
              <select
                id="itemsPerPage"
                onChange={handleLimitChange}
                value={limit}
                className="border ms-3 w-25  p-2"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
            <div className="col-md-9">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </>
      )}

      {formData?.employeeId &&
        allocatedAccessList.length === 0 &&
        !isAccessLoading && <NoRecordsAvailable />}

      {formData?.employeeId &&
        allocatedAccessList.length === 0 &&
        isAccessLoading && (
          <div className="centered-loader">
            <ThreeDots height="100" width="100" color="blue" />
          </div>
        )}

      {/* Hidden modal trigger */}
      <a
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropAccessDeallocate"
        className="open-modal-access-deallocation visually-hidden"
      >
        click
      </a>

      {/* Modal */}
      <div
        className="modal fade"
        id="staticBackdropAccessDeallocate"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title text-center flex">
                  Are you sure you want to revoke this access?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setFormData({ ...formData, reason: "" });
                    setValidationErrors({});
                  }}
                ></button>
              </div>

              <div className="modal-body">
                <div className="row mt-3">
                  <div className="col-md-2"></div>
                  <div className="col-md-4">
                    <label htmlFor="reason" className="has-asterisk">
                      Reason
                    </label>
                  </div>
                  <div className="col-md-5">
                    <textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      cols={20}
                      className="border"
                    />
                    {validationErrors.reason && (
                      <small className="text-danger">
                        {validationErrors.reason}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="justify-content-center modal-footer">
                <button
                  type="submit"
                  className={`theme-button ${isLoading ? "disabled" : ""}`}
                >
                  {isLoading ? (
                    <ThreeDots height="20" width="53" color="white" />
                  ) : (
                    "Yes"
                  )}
                </button>
                <button
                  type="button"
                  className="theme-button bg-grey mx-3 w-35 cancel-modal"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setFormData({ ...formData, reason: "" });
                    setValidationErrors({});
                  }}
                >
                  No
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const validationSchema = Yup.object().shape({
  reason: Yup.string()
    .trim()
    .min(5, "Reason must be at least 5 characters.")
    .required("Reason is required."),
});

export default AccessDeallocation;
